import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DepartmentCard = ({ department }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-xl shadow-card hover:shadow-card-lg transition-all duration-300 overflow-hidden group">
      {/* Department Header */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={department?.image}
          alt={department?.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-1">{department?.name}</h3>
              <p className="text-white/90 text-lg">{department?.duration} • {department?.level}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Department Content */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name={department?.icon} size={20} className="text-primary" />
            <span className="text-lg font-medium text-primary">{department?.category}</span>
          </div>
          <div className="flex items-center space-x-1 text-warning">
            <Icon name="Star" size={16} className="fill-current" />
            <span className="text-lg font-medium">{department?.rating}</span>
          </div>
        </div>

        <p className="text-muted-foreground text-lg mb-4 leading-relaxed">
          {isExpanded ? department?.fullDescription : department?.description}
        </p>

        {/* Key Features */}
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-black mb-2">Key Areas:</h4>
          <div className="flex flex-wrap gap-2">
            {department?.keyFeatures?.slice(0, isExpanded ? department?.keyFeatures?.length : 3)?.map((feature, index) => (
              <span
                key={index}
                className="bg-muted text-muted-foreground px-2 py-1 rounded-md text-sm"
              >
                {feature}
              </span>
            ))}
            {!isExpanded && department?.keyFeatures?.length > 3 && (
              <span className="text-primary text-sm font-medium">
                +{department?.keyFeatures?.length - 3} more
              </span>
            )}
          </div>
        </div>
        {/* Expanded Content */}
        {isExpanded && (
          <div className="space-y-4 border-t border-border pt-4">
            {/* Faculty Highlights */}
            <div>
              <h4 className="text-lg font-semibold text-black mb-2">Faculty Expertise:</h4>
              <div className="space-y-2">
                {department?.facultyHighlights?.map((faculty, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden">
                      <Image
                        src={faculty?.photo}
                        alt={faculty?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-xl font-medium text-foreground">{faculty?.name}</p>
                      <p className="text-lg text-muted-foreground">{faculty?.expertise}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-primary text-lg font-medium hover:text-primary/80 transition-colors duration-200"
          >
            {isExpanded ? 'Show Less' : 'Learn More'}
          </button>
          <div className="flex items-center space-x-2">
            <Link to="/courses">
              <Button variant="default" size="lg" iconName="ArrowRight" iconPosition="right">
                View Courses
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentCard;